import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Package, AlertTriangle, XCircle, TrendingUp, Euro, Clock, ArrowRight, ClipboardList, Wrench, PackageMinus } from "lucide-react";

function Stat({ label, value, sub, color = "text-white", icon: Icon, testId }) {
  return (
    <div className="gm-card p-5" data-testid={testId}>
      <div className="flex items-start justify-between mb-3">
        <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-500">{label}</div>
        {Icon && <Icon size={18} className="text-zinc-600" />}
      </div>
      <div className={`font-display text-4xl font-black ${color}`}>{value}</div>
      {sub && <div className="text-xs text-zinc-500 mt-2 font-mono-data">{sub}</div>}
    </div>
  );
}

export default function DashboardPage() {
  const [stats, setStats] = useState(null);
  const [woStats, setWoStats] = useState(null);
  const [daFatturare, setDaFatturare] = useState(null);
  const [smontaggi, setSmontaggi] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [r1, r2, r3, r4] = await Promise.all([
          api.get("/dashboard/stats"),
          api.get("/work-orders/stats/dashboard").catch(()=>({data:null})),
          api.get("/work-orders/da-fatturare").catch(()=>({data:null})),
          api.get("/smontaggi", { params: { stato: "aperti" } }).catch(()=>({data:null})),
        ]);
        setStats(r1.data); setWoStats(r2.data); setDaFatturare(r3.data); setSmontaggi(r4.data);
      } finally { setLoading(false); }
    })();
  }, []);

  if (loading) return <div className="p-8 text-zinc-500 uppercase tracking-widest text-sm">Caricamento dati...</div>;
  if (!stats) return null;

  return (
    <div className="p-4 md:p-8 max-w-7xl mx-auto">
      <div className="flex items-end justify-between mb-8 flex-wrap gap-3">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-zinc-500 mb-2">Centro di controllo</div>
          <h1 className="font-display text-4xl md:text-5xl font-black uppercase tracking-tight">Dashboard</h1>
        </div>
        <Link to="/prodotti/nuovo" className="gm-btn-primary h-12 px-6 rounded-sm flex items-center gap-2" data-testid="dashboard-new-product">
          <Package size={18} /> Nuovo Prodotto
        </Link>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-5 mb-8">
        <Stat testId="stat-total-products" label="Totale ricambi" value={stats.total_products} icon={Package} />
        <Stat testId="stat-sotto-scorta" label="Sotto scorta" value={stats.sotto_scorta_count} color="text-[#FF9F0A]" icon={AlertTriangle} />
        <Stat testId="stat-esauriti" label="Esauriti" value={stats.esauriti_count} color="text-[#FF3B30]" icon={XCircle} />
        <Stat testId="stat-valore" label="Valore magazzino" value={`€ ${stats.valore_magazzino.toLocaleString("it-IT", {minimumFractionDigits: 2})}`} icon={Euro} />
      </div>

      {daFatturare && daFatturare.count > 0 && (
        <div className="gm-card border-[#FF3B30]/40 bg-[#FF3B30]/5 p-5 mb-8" data-testid="dashboard-da-fatturare-alert">
          <div className="flex items-start justify-between flex-wrap gap-3 mb-3">
            <div className="flex items-center gap-3">
              <div className="h-12 w-12 bg-[#FF3B30]/20 border border-[#FF3B30]/40 flex items-center justify-center">
                <AlertTriangle className="text-[#FF3B30]" size={22} />
              </div>
              <div>
                <div className="text-xs uppercase tracking-[0.2em] text-[#FF3B30] font-bold">Attenzione — Fatturazione</div>
                <h3 className="font-display text-2xl font-black uppercase tracking-tight">
                  {daFatturare.count} {daFatturare.count === 1 ? "scheda" : "schede"} da fatturare
                </h3>
                <div className="text-sm text-zinc-400">Interventi completati non ancora fatturati — valore totale <span className="text-[#4CAF50] font-mono-data font-bold">€ {daFatturare.totale_da_fatturare.toLocaleString("it-IT", {minimumFractionDigits: 2})}</span></div>
              </div>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2 max-h-72 overflow-y-auto">
            {daFatturare.items.slice(0, 10).map(w => (
              <Link key={w.id} to={`/schede/${w.id}`} className="flex items-center justify-between gap-2 p-2.5 border border-zinc-800 hover:border-[#FF3B30]/50 hover:bg-zinc-900/50">
                <div className="min-w-0">
                  <div className="font-mono-data text-xs text-[#4CAF50]">{w.numero}</div>
                  <div className="text-sm font-semibold truncate">{w.customer_name}</div>
                  <div className="text-xs text-zinc-500 truncate">{w.machine_label}</div>
                </div>
                <div className="text-right shrink-0">
                  <div className="font-display text-xl font-black text-[#FF3B30]">€ {(w.totale||0).toFixed(2)}</div>
                  <div className="text-[10px] uppercase tracking-widest text-zinc-500">{w.stato}</div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      )}

      {smontaggi && smontaggi.count > 0 && (
        <div className="gm-card border-[#FF9F0A]/40 bg-[#FF9F0A]/5 p-5 mb-8" data-testid="dashboard-smontaggi-alert">
          <div className="flex items-start justify-between flex-wrap gap-3 mb-3">
            <div className="flex items-center gap-3">
              <div className="h-12 w-12 bg-[#FF9F0A]/20 border border-[#FF9F0A]/40 flex items-center justify-center">
                <PackageMinus className="text-[#FF9F0A]" size={22} />
              </div>
              <div>
                <div className="text-xs uppercase tracking-[0.2em] text-[#FF9F0A] font-bold">Attenzione — Mezzi nuovi cannibalizzati</div>
                <h3 className="font-display text-2xl font-black uppercase tracking-tight">
                  {smontaggi.count} {smontaggi.count === 1 ? "mezzo da rimpinguare" : "mezzi da rimpinguare"}
                </h3>
                <div className="text-sm text-zinc-400">Ricambi prelevati da macchine in esposizione non ancora rimontati</div>
              </div>
            </div>
            <Link to="/smontaggi" className="text-xs uppercase tracking-widest text-[#FF9F0A] font-bold flex items-center gap-1 hover:underline">Gestisci <ArrowRight size={14} /></Link>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2 max-h-56 overflow-y-auto">
            {smontaggi.items.slice(0, 6).map(sm => (
              <Link key={sm.id} to="/smontaggi" className="flex items-center justify-between gap-2 p-2.5 border border-zinc-800 hover:border-[#FF9F0A]/50 hover:bg-zinc-900/50">
                <div className="min-w-0">
                  <div className="font-mono-data text-xs text-[#4CAF50]">{sm.numero}</div>
                  <div className="text-sm font-semibold truncate">{[sm.marca, sm.modello].filter(Boolean).join(" ") || "Mezzo"} • {sm.matricola}</div>
                  <div className="text-xs text-zinc-500 truncate">{sm.ricambio_descrizione}</div>
                </div>
                <span className={`gm-badge shrink-0 ${sm.stato === "ordinato" ? "gm-badge-warning" : "gm-badge-danger"}`}>{sm.stato === "ordinato" ? "ORDINATO" : "DA RIMPINGUARE"}</span>
              </Link>
            ))}
          </div>
        </div>
      )}

      {woStats && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-5 mb-8">
          <Link to="/schede?stato=aperta" className="block"><Stat testId="stat-wo-aperte" label="Schede aperte" value={woStats.by_state?.aperta || 0} icon={ClipboardList} color="text-[#4CAF50]" /></Link>
          <Link to="/schede?stato=in_lavorazione" className="block"><Stat label="In lavorazione" value={woStats.by_state?.in_lavorazione || 0} icon={Wrench} /></Link>
          <Link to="/schede?stato=in_attesa_ricambi" className="block"><Stat label="Attesa ricambi" value={woStats.by_state?.in_attesa_ricambi || 0} color="text-[#FF9F0A]" icon={Clock} /></Link>
          <Stat label="Schede urgenti" value={woStats.urgenti?.length || 0} color="text-[#FF3B30]" icon={AlertTriangle} />
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        {/* Sotto scorta */}
        <div className="gm-card">
          <div className="p-5 border-b border-zinc-800 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <AlertTriangle className="text-[#FF9F0A]" size={18} />
              <h3 className="font-display text-xl font-bold uppercase tracking-tight">Da Riordinare</h3>
            </div>
            <span className="gm-badge gm-badge-warning">{stats.sotto_scorta.length + stats.esauriti.length}</span>
          </div>
          <div className="divide-y divide-zinc-800/50 max-h-96 overflow-y-auto">
            {[...stats.esauriti, ...stats.sotto_scorta].length === 0 && (
              <div className="p-6 text-sm text-zinc-500">Nessun ricambio da riordinare. Magazzino in ordine.</div>
            )}
            {[...stats.esauriti, ...stats.sotto_scorta].map(p => (
              <Link key={p.id} to={`/prodotti/${p.id}`} className="block p-4 hover:bg-zinc-900/50 transition-colors" data-testid="low-stock-item">
                <div className="flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <div className="font-mono-data text-xs text-[#4CAF50]">{p.codice_interno}</div>
                    <div className="text-sm font-semibold truncate mt-0.5">{p.descrizione}</div>
                    <div className="text-xs text-zinc-500 mt-0.5">{p.marca} • {p.posizione || "—"}</div>
                  </div>
                  <div className="text-right shrink-0">
                    <div className={`font-display text-2xl font-black ${p.giacenza === 0 ? "text-[#FF3B30]" : "text-[#FF9F0A]"}`}>
                      {p.giacenza}
                    </div>
                    <div className="text-[10px] uppercase tracking-widest text-zinc-500">min: {p.quantita_minima}</div>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>

        {/* Top moved */}
        <div className="gm-card">
          <div className="p-5 border-b border-zinc-800 flex items-center gap-2">
            <TrendingUp className="text-[#4CAF50]" size={18} />
            <h3 className="font-display text-xl font-bold uppercase tracking-tight">Più Movimentati (60g)</h3>
          </div>
          <div className="divide-y divide-zinc-800/50">
            {stats.top_moved.length === 0 && (
              <div className="p-6 text-sm text-zinc-500">Nessun movimento recente.</div>
            )}
            {stats.top_moved.map((m, i) => (
              <div key={i} className="p-4 flex items-center gap-3">
                <div className="font-display text-3xl font-black text-zinc-700 w-8">{i+1}</div>
                <div className="min-w-0 flex-1">
                  <div className="font-mono-data text-xs text-[#4CAF50]">{m.codice_interno}</div>
                  <div className="text-sm font-semibold truncate">{m.descrizione}</div>
                </div>
                <div className="text-right">
                  <div className="font-display text-2xl font-black">{m.totale}</div>
                  <div className="text-[10px] uppercase tracking-widest text-zinc-500">pezzi</div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Fermi */}
        <div className="gm-card lg:col-span-2">
          <div className="p-5 border-b border-zinc-800 flex items-center gap-2">
            <Clock className="text-zinc-500" size={18} />
            <h3 className="font-display text-xl font-bold uppercase tracking-tight">Fermi da 90+ giorni</h3>
          </div>
          {stats.fermi.length === 0 ? (
            <div className="p-6 text-sm text-zinc-500">Nessun ricambio fermo da molto tempo.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="gm-table">
                <thead>
                  <tr>
                    <th>Codice</th><th>Descrizione</th><th>Posizione</th><th className="text-right">Giac.</th><th></th>
                  </tr>
                </thead>
                <tbody>
                  {stats.fermi.map(p => (
                    <tr key={p.id}>
                      <td className="font-mono-data text-[#4CAF50]">{p.codice_interno}</td>
                      <td>{p.descrizione}</td>
                      <td className="font-mono-data text-zinc-400">{p.posizione || "—"}</td>
                      <td className="text-right font-mono-data">{p.giacenza}</td>
                      <td className="text-right">
                        <Link to={`/prodotti/${p.id}`} className="text-[#4CAF50] hover:underline inline-flex items-center gap-1 text-xs uppercase tracking-wider">
                          Apri <ArrowRight size={14} />
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
